import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { isCustomBudgetProposal } from '../../BudgetProposal/Resource/hooks/budgetProposal';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';

type BudgetUserExerciceSetting = {
    exercice: number;
    isReportedAmountUsed: boolean;
};

const fetchBudgetUserExerciceSetting = async (
    exercice: number,
): Promise<BudgetUserExerciceSetting> => {
    const response = await RQAPICallWithData<BudgetUserExerciceSetting>({
        endpoint: `budget_user_settings/exercice/${exercice}`,
        method: 'GET',
    });

    return response.content.data || {};
};

export const useGetBudgetUserExerciceSettings = (
    exercice: number,
): {
    budgetReportExerciceData: BudgetUserExerciceSetting;
    isLoading: boolean;
} => {
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);

    const fetchSettings = async () =>
        await fetchBudgetUserExerciceSetting(exercice);

    const { data, isLoading } = useQuery({
        queryKey: [`budget_user_exercice_settings_query_${exercice}`],
        queryFn: fetchSettings,
        enabled: !isSchool,
    });

    return {
        budgetReportExerciceData:
            isLoading || isSchool
                ? { exercice, isReportedAmountUsed: false }
                : (data as BudgetUserExerciceSetting),
        isLoading,
    };
};

export const useMutateBudgetUserExerciceSetting = (): {
    mutate: (arg1: BudgetUserExerciceSetting) => void;
} => {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: (body: BudgetUserExerciceSetting) =>
            RQAPICallWithData<BudgetUserExerciceSetting>({
                endpoint: 'budget_user_settings/exercice',
                method: 'PATCH',
                body,
            }),

        onSuccess: (response) => {
            queryClient.setQueryData(
                [
                    `budget_user_exercice_settings_query_${response.content.data.exercice}`,
                ],
                () => ({
                    ...response.content.data,
                }),
            );
        },
    });
    return mutation;
};
